import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  PoweroffOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Avatar,
  Button,
  Divider,
  Layout,
  Menu,
  Popconfirm,
  Tooltip
} from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { AppContext } from '../../../AppContext';
import {
  ChevronDown,
  ChevronRight,
  CollapseLogoIcon,
  EmailIcon,
  MembersIcon,
  MessageIcon
} from '../../../assets/svg';
import {
  ACTIVE_TABS,
  DIVIDER,
  MODULES,
  PROJECT_ENV,
  PROJECT_KEYS,
  ROUTES,
  TABLET_BREAKPOINT,
  USERS_ROLE
} from '../../../common/constants';
import LoaderComponent from '../../../components/LoaderComponent';
import useInfiniteScroll from '../../../hooks/useInfiniteScroll';
import { DELETE_PROJECT } from '../../../modules/projects/graphql/Mutations';
import {
  PROJECT_ENVIRONMENT,
  PROJECT_LISTING
} from '../../../modules/projects/graphql/Queries';
import './sidebar.less';

const { Sider } = Layout;
const { SubMenu } = Menu;

function getWindowSize() {
  // eslint-disable-next-line no-undef
  const { innerWidth, innerHeight } = window;
  return { innerWidth, innerHeight };
}

function Sidebar({ location: { pathname }, history }) {
  const { getCurrentRole, getCurrentUser, state, dispatch } = useContext(
    AppContext
  );
  const userRole = getCurrentRole();
  const currentUser = getCurrentUser();
  const finalPath = `/${pathname?.split('/')[3]}`;
  const [projectsListing, setProjectsListing] = useState([]);
  const [collapsed, setCollapsed] = useState(true);
  const [openKeys, setOpenKeys] = useState(
    // eslint-disable-next-line no-undef
    [localStorage.getItem(PROJECT_KEYS)] || [`${pathname?.split('/')[2]}`]
  );
  const final = `${openKeys}${finalPath}`;
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const [isIndex, setIsIndex] = useState(0);
  const [windowSize, setWindowSize] = useState(getWindowSize());
  const [projectSkip, setProjectSkip] = useState(0);
  const projectLimit = 10;
  const [isProjectEnd, setIsProjectEnd] = useState(false);
  const [projectLoading, setProjectLoading] = useState(false);
  const [isProjectDeleted, setIsProjectDeleted] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isFetchMoreProjectsLoading, setIsFetchMoreProjectsLoading] = useState(
    false
  );
  // eslint-disable-next-line no-undef
  const projectId = localStorage.getItem(PROJECT_KEYS);
  // eslint-disable-next-line no-undef
  const projectTabs = localStorage.getItem(ACTIVE_TABS);

  const [projectsList] = useLazyQuery(PROJECT_LISTING, {
    onCompleted: (response) => {
      setIsFetchMoreProjectsLoading(false);
      if (response?.projects?.projects?.length < projectLimit) {
        setIsProjectEnd(true);
        setIsActive(false);
      }
      if (isProjectDeleted) {
        history?.push(ROUTES?.DASHBOARD);
      }
      if (
        response?.projects?.projects?.length === 0 &&
        !pathname?.includes(ROUTES?.ADDPROJECT) &&
        projectsList?.length === 0
      ) {
        history?.push(ROUTES?.DASHBOARD);
      }
      setProjectsListing([...projectsListing, ...response?.projects?.projects]);
      if (response?.projects?.projects?.length > 0) {
        if (state?.createNewProject) {
          // eslint-disable-next-line no-undef
          localStorage.setItem(PROJECT_KEYS, [
            response?.projects?.projects?.[0]?.id
          ]);
          setOpenKeys([response?.projects?.projects?.[0]?.id]);
          history?.push(
            `${ROUTES?.PROJECTS}/${response?.projects?.projects?.[0]?.id}${ROUTES?.EMAIL}${ROUTES?.SETTING}`
          );
          dispatch({
            type: 'CREATE_NEW_PROJECT',
            data: false
          });
        } else if (projectId && projectTabs) {
          history?.push(
            `${ROUTES?.PROJECTS}/${projectId}${ROUTES?.EMAIL}${projectTabs}`
          );
        } else {
          // eslint-disable-next-line no-undef
          localStorage.setItem(PROJECT_KEYS, [
            response?.projects?.projects?.[0]?.id
          ]);
          setOpenKeys([response?.projects?.projects?.[0]?.id]);
          history?.push(
            `${ROUTES?.PROJECTS}/${response?.projects?.projects?.[0]?.id}${ROUTES?.EMAIL}`
          );
        }
      }
      setProjectLoading(false);
    },
    fetchPolicy: 'network-only'
  });

  const [executeProjectEnvironment] = useLazyQuery(PROJECT_ENVIRONMENT, {
    onCompleted: (res) => {
      dispatch({
        type: 'SET_CURRENT_ROLE',
        data: res?.projectEnvironment?.environments?.[0]?.permission
      });
    },
    fetchPolicy: 'network-only',
    onError() {}
  });

  const [deleteProject] = useMutation(DELETE_PROJECT, {
    onError: () => {}
  });

  useEffect(() => {
    setIsActive(true);
    setProjectLoading(true);
    projectsList({
      variables: {
        filter: {
          skip: 0,
          limit: projectLimit,
          search: '',
          sortBy: 'createdAtDESC'
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (projectId) {
      executeProjectEnvironment({
        variables: {
          projectId: projectId
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    function handleWindowResize() {
      setWindowSize(getWindowSize());
    }
    // eslint-disable-next-line no-undef
    window?.addEventListener('resize', handleWindowResize);
  }, []);

  useEffect(() => {
    if (windowSize?.innerWidth < TABLET_BREAKPOINT) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [windowSize?.innerWidth]);

  const toggle = () => {
    setCollapsed(!collapsed);
  };

  const onMenuSelect = (e) => {
    history?.push(`${ROUTES?.PROJECTS}/${e?.key}`);
  };

  useEffect(() => {
    setOpenKeys([`${pathname?.split('/')[2]}`]);
  }, [pathname]);

  const onOpenChange = (keys) => {
    const latestOpenKey = keys?.find((key) => openKeys?.indexOf(key) === -1);
    setOpenKeys([latestOpenKey]);
    // eslint-disable-next-line no-undef
    localStorage.setItem(PROJECT_KEYS, [latestOpenKey]);
  };

  const handleSidebar = (e, id) => {
    setIsIndex(e);
    setIsMenuOpen(!isMenuOpen);
    history?.push(`${ROUTES?.PROJECTS}/${id}${ROUTES?.EMAIL}`);
  };

  const handleScroll = () => {
    if (!isProjectEnd && !isFetchMoreProjectsLoading && !projectLoading) {
      if (projectsListing?.length > 0) {
        setIsFetchMoreProjectsLoading(true);
        const newSkip = projectSkip + projectLimit;
        setProjectSkip(newSkip);
        projectsList({
          variables: {
            filter: {
              skip: newSkip,
              limit: projectLimit,
              search: '',
              sortBy: 'createdAtDESC'
            }
          }
        });
      }
    }
  };

  const infiniteScrollRef = useInfiniteScroll(handleScroll, isActive);

  const deleteProjectFun = (project) => {
    setProjectLoading(true);
    setIsProjectDeleted(false);
    deleteProject({
      variables: {
        id: project?.id
      }
    }).then(() => {
      setProjectsListing([]);
      setIsProjectDeleted(true);
      setIsProjectEnd(false);
      setIsActive(true);
      projectsList({
        variables: {
          filter: {
            skip: 0,
            limit: projectLimit,
            search: '',
            sortBy: 'createdAtDESC'
          }
        }
      });
    });
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
    >
      {projectLoading ? (
        <LoaderComponent
          size="large"
          setHeight="60"
          spinning={projectLoading}
        />
      ) : (
        <>
          <div
            className={`main-logo text-center mt-20 font-xx-large ${
              !collapsed ? 'open' : 'close'
            }`}
          >
            {!collapsed ? (
              'Sendbay'
            ) : (
              <svg
                width="42"
                height="45"
                viewBox="0 0 42 45"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.728 34.224C8.23467 34.224 6.89067 33.968 5.696 33.456C4.50133 32.9227 3.56267 32.176 2.88 31.216C2.19733 30.256 1.856 29.136 1.856 27.856H5.76C5.84533 28.816 6.21867 29.6053 6.88 30.224C7.56267 30.8427 8.512 31.152 9.728 31.152C10.9867 31.152 11.968 30.8533 12.672 30.256C13.376 29.6373 13.728 28.848 13.728 27.888C13.728 27.1413 13.504 26.5333 13.056 26.064C12.6293 25.5947 12.0853 25.232 11.424 24.976C10.784 24.72 9.888 24.4427 8.736 24.144C7.28533 23.76 6.10133 23.376 5.184 22.992C4.288 22.5867 3.52 21.968 2.88 21.136C2.24 20.304 1.92 19.1947 1.92 17.808C1.92 16.528 2.24 15.408 2.88 14.448C3.52 13.488 4.416 12.752 5.568 12.24C6.72 11.728 8.05333 11.472 9.568 11.472C11.7227 11.472 13.4827 12.016 14.848 13.104C16.2347 14.1707 17.0027 15.6427 17.152 17.52H13.12C13.056 16.7093 12.672 16.016 11.968 15.44C11.264 14.864 10.336 14.576 9.184 14.576C8.13867 14.576 7.28533 14.8427 6.624 15.376C5.96267 15.9093 5.632 16.6773 5.632 17.68C5.632 18.3627 5.83467 18.928 6.24 19.376C6.66667 19.8027 7.2 20.144 7.84 20.4C8.48 20.656 9.35467 20.9333 10.464 21.232C11.936 21.6373 13.1307 22.0427 14.048 22.448C14.9867 22.8533 15.776 23.4827 16.416 24.336C17.0773 25.168 17.408 26.288 17.408 27.696C17.408 28.8267 17.0987 29.8933 16.48 30.896C15.8827 31.8987 14.9973 32.7093 13.824 33.328C12.672 33.9253 11.3067 34.224 9.728 34.224ZM25.6805 18.992C26.2992 18.1387 27.1418 17.4453 28.2085 16.912C29.2965 16.3573 30.5018 16.08 31.8245 16.08C33.3818 16.08 34.7898 16.4533 36.0485 17.2C37.3072 17.9467 38.2992 19.0133 39.0245 20.4C39.7498 21.7653 40.1125 23.3333 40.1125 25.104C40.1125 26.8747 39.7498 28.464 39.0245 29.872C38.2992 31.2587 37.2965 32.3467 36.0165 33.136C34.7578 33.904 33.3605 34.288 31.8245 34.288C30.4592 34.288 29.2432 34.0213 28.1765 33.488C27.1312 32.9547 26.2992 32.272 25.6805 31.44V34H22.0325V10.32H25.6805V18.992ZM36.4005 25.104C36.4005 23.888 36.1445 22.8427 35.6325 21.968C35.1418 21.072 34.4805 20.4 33.6485 19.952C32.8378 19.4827 31.9632 19.248 31.0245 19.248C30.1072 19.248 29.2325 19.4827 28.4005 19.952C27.5898 20.4213 26.9285 21.104 26.4165 22C25.9258 22.896 25.6805 23.952 25.6805 25.168C25.6805 26.384 25.9258 27.4507 26.4165 28.368C26.9285 29.264 27.5898 29.9467 28.4005 30.416C29.2325 30.8853 30.1072 31.12 31.0245 31.12C31.9632 31.12 32.8378 30.8853 33.6485 30.416C34.4805 29.9253 35.1418 29.2213 35.6325 28.304C36.1445 27.3867 36.4005 26.32 36.4005 25.104Z"
                  fill="#263238"
                />
              </svg>
            )}
          </div>
          <div className="heading">
            <div onClick={toggle} className="gx-icon-btn icon pointer">
              {collapsed ? (
                <span>
                  <CollapseLogoIcon height={20} width={20} />
                </span>
              ) : (
                <div className="d-flex align-center justify-between fill-width">
                  <span>{MODULES?.PROJECTS}</span>
                  <Tooltip placement="top" title="Add">
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      className="primary-button ml-10"
                      onClick={(e) => {
                        e?.stopPropagation();
                        e?.preventDefault();
                        // eslint-disable-next-line no-undef
                        localStorage.removeItem(PROJECT_ENV);
                        setIsIndex();
                        setOpenKeys();
                        history?.push(
                          `${ROUTES?.PROJECTS}${ROUTES?.ADDPROJECT}`
                        );
                      }}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
            <div className="gx-sidebar-content">
              <div className="sb-content">
                {!collapsed ? (
                  <Menu
                    mode="inline"
                    openKeys={openKeys}
                    onOpenChange={onOpenChange}
                    selectedKeys={[final]}
                    expandIcon={<ChevronDown />}
                    onClick={onMenuSelect}
                    onSelect={onMenuSelect}
                    className="pointer"
                    theme="dark"
                  >
                    {projectsListing?.map((project, index) => {
                      return (
                        <SubMenu
                          key={project?.id}
                          title={
                            <div ref={infiniteScrollRef}>{project?.name}</div>
                          }
                          onTitleClick={() => {
                            handleSidebar(index, project?.id);
                          }}
                          icon={
                            userRole === USERS_ROLE?.OWNER && (
                              <span>
                                <Tooltip placement="top" title="Edit">
                                  <EditOutlined
                                    onClick={(e) => {
                                      e?.stopPropagation();
                                      history?.push(
                                        `${ROUTES?.PROJECTS}/${project?.id}${ROUTES?.EDIT_PROJECT}`
                                      );
                                    }}
                                  />
                                </Tooltip>
                                <Popconfirm
                                  title={`Are you sure you want to delete ${project?.name} project?`}
                                  onConfirm={() => deleteProjectFun(project)}
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <Tooltip placement="top" title="Delete">
                                    <DeleteOutlined
                                      onClick={(e) => {
                                        e?.stopPropagation();
                                      }}
                                    />
                                  </Tooltip>
                                </Popconfirm>
                              </span>
                            )
                          }
                        >
                          <Menu.Item
                            className="sub-menu-divider"
                            key={`${project?.id}${DIVIDER}`}
                          >
                            <Divider />
                          </Menu.Item>
                          <Menu.Item
                            key={`${project?.id}${ROUTES?.EMAIL}`}
                            icon={<EmailIcon />}
                          >
                            <span>{MODULES?.EMAIL}</span>
                          </Menu.Item>
                          <Menu.Item
                            key={`${project?.id}${ROUTES?.SMS}`}
                            icon={<MessageIcon />}
                          >
                            <span>{MODULES?.SMS}</span>
                          </Menu.Item>
                          <Menu.Item
                            key={`${project?.id}${ROUTES?.MEMBERS}`}
                            icon={<MembersIcon />}
                          >
                            <span>{MODULES?.MEMBERS}</span>
                          </Menu.Item>
                        </SubMenu>
                      );
                    })}
                  </Menu>
                ) : (
                  <>
                    {projectsListing?.map((project, index) => {
                      return (
                        <nav
                          key={project?.id}
                          className={`${
                            index === isIndex ? 'menu-theme' : 'no-menu-theme'
                          } pointer`}
                          ref={infiniteScrollRef}
                        >
                          <ul className="text-center">
                            <li key={project?.id}>
                              <div
                                className="pt-10 pb-10"
                                onClick={() => {
                                  handleSidebar(index, project?.id);
                                }}
                              >
                                <Avatar
                                  className="sb-project-avatar"
                                  id={index}
                                >
                                  <span className="sb-project-avatar-text">
                                    {project?.name?.[0]?.toUpperCase()}
                                  </span>
                                </Avatar>
                              </div>
                              <div
                                className={
                                  index === isIndex
                                    ? 'sb-open-menu'
                                    : 'sb-close-menu'
                                }
                              >
                                <ul className="pt-15 pb-15" key={project?.id}>
                                  <li
                                    onClick={() => {
                                      history?.push(
                                        `${ROUTES?.PROJECTS}/${project?.id}${ROUTES?.EMAIL}`
                                      );
                                    }}
                                    key={`${project?.id}${ROUTES?.EMAIL}`}
                                    className={`${
                                      index === isIndex &&
                                      finalPath === ROUTES?.EMAIL
                                        ? 'active'
                                        : ''
                                    } mt-10`}
                                  >
                                    <EmailIcon />
                                  </li>
                                  <li
                                    onClick={() => {
                                      history?.push(
                                        `${ROUTES?.PROJECTS}/${project?.id}${ROUTES?.SMS}`
                                      );
                                    }}
                                    key={`${project?.id}${ROUTES?.SMS}`}
                                    className={`${
                                      index === isIndex &&
                                      finalPath === ROUTES?.SMS
                                        ? 'active'
                                        : ''
                                    } mt-10`}
                                  >
                                    <MessageIcon />
                                  </li>
                                  <li
                                    onClick={() => {
                                      history?.push(
                                        `${ROUTES?.PROJECTS}/${project?.id}${ROUTES?.MEMBERS}`
                                      );
                                    }}
                                    key={`${project?.id}${ROUTES?.MEMBERS}`}
                                    className={`${
                                      index === isIndex &&
                                      finalPath === ROUTES?.MEMBERS
                                        ? 'active'
                                        : ''
                                    } mt-10`}
                                  >
                                    <MembersIcon />
                                  </li>
                                </ul>
                              </div>
                            </li>
                          </ul>
                        </nav>
                      );
                    })}
                  </>
                )}
                {isFetchMoreProjectsLoading && (
                  <LoaderComponent size="small" setHeight={5} />
                )}
              </div>
            </div>
          </div>
          <div className="login-icon">
            <Menu
              mode="vertical"
              className="pointer login-menu"
              theme="dark"
              expandIcon={!collapsed ? <ChevronRight /> : null}
            >
              <SubMenu
                key="logout"
                className="user-login-email"
                title={
                  !collapsed ? (
                    <div className="avatar-footer d-flex">
                      <Avatar size="large" icon={<UserOutlined />} />
                      <span className="login-name">{currentUser?.email}</span>
                    </div>
                  ) : (
                    <Avatar
                      size="large"
                      className="logout-avitar"
                      icon={<PoweroffOutlined />}
                    />
                  )
                }
                onClick={() => history?.push(ROUTES?.LOGOUT)}
              >
                <Menu.Item
                  className="logout-link"
                  key={`${ROUTES?.LOGOUT}`}
                  onClick={() => history?.push(ROUTES?.LOGOUT)}
                >
                  <span>Logout</span>
                </Menu.Item>
              </SubMenu>
            </Menu>
          </div>
        </>
      )}
    </Sider>
  );
}

export default withRouter(Sidebar);
